var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Form, Formik } from "formik";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { MultiColumnFormFields } from "@components/common/form/FormFields";
import { PeekButton, PeekDialogActions, PeekDialogContent, PeekDialogTitle, PeekDivider, PeekStack, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import messages from "./messages.i18n";
import { ScheduleFormFields, ScheduleFormInitialValues, ScheduleFormValidationSchema } from "./ScheduleFormConfig";
import { format } from "date-fns";
import { ScanRequestStatus, useBulkUpdateScanRequests, useError, useLoader, useNotification, useRefetch, useSendScheduledScansConfirmation, } from "@piiqtechnologies/ui-lib";
import { AlertTriangle } from "@piiqtechnologies/svg-icons";
export const ScheduleForm = ({ selectedScanRequests, onConfirmSchedule }) => {
    var _a;
    const intl = useIntl();
    const theme = useTheme();
    const { setLoader } = useLoader();
    const { setNotification } = useNotification();
    const { setError } = useError();
    const { setRefetch } = useRefetch();
    const [updateScanRequests, _scanRequestsUpdated, updateScanRequestsLoading, updateScanRequestsError] = useBulkUpdateScanRequests();
    const [sendConfirmationEmail, confirmationEmailResult, confirmationEmailLoading, confirmationEmailError] = useSendScheduledScansConfirmation();
    const onScheduleForm = (values) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const scheduledDay = new Date(values.scheduledFor);
        const newValues = Object.assign(Object.assign({}, values), { scannerArrivalWindow: {
                from: format(new Date(scheduledDay.getFullYear(), scheduledDay.getMonth(), Number.parseInt((_a = values.scheduledFor) === null || _a === void 0 ? void 0 : _a.split("/")[1]) || 1, ((_c = (_b = values.scannerArrivalWindow) === null || _b === void 0 ? void 0 : _b.from) === null || _c === void 0 ? void 0 : _c.getHours()) || 0, ((_e = (_d = values.scannerArrivalWindow) === null || _d === void 0 ? void 0 : _d.from) === null || _e === void 0 ? void 0 : _e.getMinutes()) || 0), "h:mm aaaa"),
                to: format(new Date(scheduledDay.getFullYear(), scheduledDay.getMonth(), Number.parseInt((_f = values.scheduledFor) === null || _f === void 0 ? void 0 : _f.split("/")[1]) || 1, ((_h = (_g = values.scannerArrivalWindow) === null || _g === void 0 ? void 0 : _g.to) === null || _h === void 0 ? void 0 : _h.getHours()) || 0, ((_k = (_j = values.scannerArrivalWindow) === null || _j === void 0 ? void 0 : _j.to) === null || _k === void 0 ? void 0 : _k.getMinutes()) || 0), "h:mm aaaa"),
            } });
        handleScheduleRequests(newValues);
    };
    const handleScheduleRequests = (scheduleData) => __awaiter(void 0, void 0, void 0, function* () {
        const schedulePayload = selectedScanRequests === null || selectedScanRequests === void 0 ? void 0 : selectedScanRequests.map(({ _id }) => (Object.assign({ _id, status: ScanRequestStatus.SCHEDULED }, scheduleData)));
        yield updateScanRequests(schedulePayload);
        sendConfirmationEmail(selectedScanRequests === null || selectedScanRequests === void 0 ? void 0 : selectedScanRequests.map(({ _id }) => _id));
    });
    useEffect(() => {
        setLoader(updateScanRequestsLoading, "updateScanRequestLoading");
        return () => setLoader(false, "updateScanRequestLoading");
    }, [updateScanRequestsLoading]);
    useEffect(() => {
        setLoader(confirmationEmailLoading, "confirmationEmailLoading");
        return () => setLoader(false, "confirmationEmailLoading");
    }, [confirmationEmailLoading]);
    useEffect(() => {
        if (updateScanRequestsError) {
            setError(updateScanRequestsError.message);
        }
        else if (confirmationEmailError) {
            setError(confirmationEmailError.message);
        }
    }, [updateScanRequestsError, confirmationEmailError]);
    useEffect(() => {
        if (confirmationEmailResult) {
            setNotification(intl.formatMessage(messages.scheduledConfirm, {
                space: selectedScanRequests.map(({ space }) => space).join(", "),
            }));
            onConfirmSchedule();
            setRefetch();
        }
    }, [confirmationEmailResult]);
    return (React.createElement(Formik, { onSubmit: onScheduleForm, initialValues: ScheduleFormInitialValues, validationSchema: ScheduleFormValidationSchema() },
        React.createElement(Form, null,
            React.createElement(PeekDialogTitle, null,
                React.createElement(PeekStack, null,
                    intl.formatMessage(messages.scheduleSelectScanRequestsTitle),
                    !(selectedScanRequests === null || selectedScanRequests === void 0 ? void 0 : selectedScanRequests.length) && (React.createElement(PeekStack, { direction: "row", gap: theme.spacing(0.5) },
                        React.createElement(AlertTriangle, { color: theme.palette.error.main, fontSize: 14 }),
                        React.createElement(PeekTypography, { color: "error" }, intl.formatMessage(messages.scheduleSelectScanRequestsWarning)))))),
            React.createElement(PeekDialogContent, null,
                React.createElement(PeekStack, { gap: 1.5 },
                    React.createElement(PeekDivider, { light: true }),
                    React.createElement(MultiColumnFormFields, { fullWidth: true, fields: ScheduleFormFields(selectedScanRequests && ((_a = selectedScanRequests[0]) === null || _a === void 0 ? void 0 : _a.organization)) }))),
            React.createElement(PeekDialogActions, null,
                React.createElement(PeekButton, { size: "large", type: "submit", color: "primary", variant: "contained", fullWidth: true, disabled: !(selectedScanRequests === null || selectedScanRequests === void 0 ? void 0 : selectedScanRequests.length) }, intl.formatMessage(messages.submitSchedule))))));
};
export default ScheduleForm;
